import * as React from 'react';

import { Card, CardSection } from '@lightspeed/flame/Card';
import { Flex, flameTheme } from '@lightspeed/flame/Core';
import { Icon } from '@lightspeed/flame/Icon';
import { Text, TextLink } from '@lightspeed/flame/Text';
import { useSelector } from 'react-redux';
import semver from 'semver';

import { useGetAppDownloadUrl } from 'pages/views/devices/hooks/use-get-app-download-url';
import Client from 'pages/views/devices/socketClient';
import { STATUS } from 'pages/views/devices/socketClient/types';
import { ACTIONS_DROPDOWN } from 'pages/views/devices/types';
import { APP_NAME, PROTOCOL, PROTOCOL_IFRAME } from 'pages/views/devices/utils/helper';

import { StatusLabel } from '../components/StatusLabel';
import {
  StyledFlexCircle,
  StyledFlexBox,
  LinkButtonDarkText,
  LinkButtonLightText,
  StyledInfoAlert,
  StyledDangerAlert,
  StyledWarningAlert,
  AlertText,
  StyledActionsDropdown,
} from '../device.styles';
import { getIsUnsupportedOS } from '../utils/get-is-unsupported-os';
import { handleConnect } from '../utils/handle-hub-connect';

const Hub: React.FunctionComponent = () => {
  const [isUnsupportedOS, setIsUnsupportedOS] = React.useState<{
    isOSVersionSupported: boolean;
    msg: string;
  }>();
  const [isHubLatestVersion, setIsHubLatestVersion] = React.useState<boolean | null>(null);
  const [connectButtonClicked, setConnectButtonClicked] = React.useState<boolean>(false);
  const hubConnectionStatus = useSelector(state => state.hub.connection.hub);
  const ablyConnectionStatus = useSelector(state => state.hub.connection.ably);
  const hubVersion = useSelector(state => state.hub.version);
  const { name: latestRelease } = useGetAppDownloadUrl();
  const { colors, fontSizes } = flameTheme;

  const isHubConnected = hubConnectionStatus === STATUS.CONNECTED;
  const ablyConnectionFailure = ablyConnectionStatus === STATUS.ERROR;

  React.useEffect(() => {
    !!latestRelease && !!hubVersion && semver.valid(hubVersion)
      ? setIsHubLatestVersion(
          semver.satisfies(hubVersion, `>=${latestRelease}`, { includePrerelease: true })
        )
      : null;
  }, [latestRelease, hubVersion]);

  React.useEffect(() => {
    (async () => {
      const data = await getIsUnsupportedOS();
      if (data) {
        const { isOSVersionSupported, msg } = data;
        if (!isOSVersionSupported && msg) {
          setIsUnsupportedOS({ isOSVersionSupported, msg });
        }
      }
    })();
  }, []);

  const renderDeviceCard = (
    <Card p={4} noSpacing>
      <CardSection>
        {isUnsupportedOS?.isOSVersionSupported === false && (
          <StyledWarningAlert
            type="warning"
            icon={<Icon name="warning" size="1rem" baseColor={colors['orange-500']} />}
            mb={4}
            noCloseBtn
          >
            <AlertText data-test="os-version-unsupported">
              {isUnsupportedOS?.msg}{' '}
              <a href="https://retail-support.lightspeedhq.com/hc/articles/229131008-Supported-hardware">
                Learn more
              </a>{' '}
              about upgrading your OS.
            </AlertText>
          </StyledWarningAlert>
        )}
        {isHubLatestVersion === false ? (
          <StyledInfoAlert
            type="info"
            icon={<Icon name="info" baseColor1={colors.secondary} />}
            mb={4}
          >
            <AlertText data-test="new-version-available">
              A newer version of Hub is available. Download the latest update in the app.
            </AlertText>
          </StyledInfoAlert>
        ) : null}
        {!isHubConnected && connectButtonClicked ? (
          <StyledInfoAlert
            type="info"
            icon={<Icon name="info" baseColor1={colors.secondary} />}
            mb={4}
          >
            <AlertText data-test="info-alert-manually-connect">
              If you don&apos;t see a prompt from your browser, manually open the {APP_NAME} app on
              your desktop and{' '}
              <TextLink
                onClick={() => {
                  handleConnect();
                }}
                href={`${PROTOCOL}${Client.getCFDParams()}`}
                target={PROTOCOL_IFRAME}
                fontWeight="bold"
              >
                click here to connect
              </TextLink>
              .
            </AlertText>
          </StyledInfoAlert>
        ) : null}
        {ablyConnectionFailure ? (
          <StyledDangerAlert
            type="danger"
            icon={
              <Icon
                name="danger"
                color={colors['maple-600']}
                size={fontSizes['text-l'] as string}
              />
            }
            mb={4}
          >
            <AlertText data-test="danger-alert-connection-failure">
              {APP_NAME} is having trouble connecting with Retail. If the problem persists after a
              few minutes, contact Support.
            </AlertText>
          </StyledDangerAlert>
        ) : null}
        <Flex>
          <Flex alignSelf="flex-start">
            <StyledFlexCircle>
              <img
                src="https://d2o5po5b88zacb.cloudfront.net/dist/assets/lshash-9919532/images/lightspeed-hub-logo-white.svg"
                alt="Lightspeed Icon"
                width="31px"
                height="46px"
              />
            </StyledFlexCircle>
          </Flex>
          <StyledFlexBox>
            <Flex flexDirection="column" alignItems="flex-start">
              <Flex alignItems="center" flexWrap="wrap">
                <Text mr={2} fontSize={fontSizes.text} color={colors['gray-850']} fontWeight="bold">
                  {APP_NAME}
                </Text>
                <StatusLabel
                  size="medium"
                  status={isHubConnected ? STATUS.CONNECTED : STATUS.DISCONNECTED}
                />
              </Flex>
              <Text mb={1} mt={1} color={colors['gray-600']}>
                Lightspeed Device Management App
              </Text>
              <Flex style={{ position: 'relative' }}>
                {isHubConnected ? (
                  <StyledActionsDropdown
                    callback={() => setConnectButtonClicked(false)}
                    type={ACTIONS_DROPDOWN.HUB}
                  />
                ) : null}
              </Flex>
            </Flex>
            <Flex alignSelf="flex-start">
              {isHubConnected ? (
                <LinkButtonDarkText
                  fill
                  href={`${PROTOCOL}${Client.getCFDParams()}`}
                  target={PROTOCOL_IFRAME}
                  variant="default"
                >
                  {`Open ${APP_NAME}`}
                </LinkButtonDarkText>
              ) : (
                <LinkButtonLightText
                  onClick={() => {
                    handleConnect();
                    setConnectButtonClicked(true);
                  }}
                  fill
                  href={`${PROTOCOL}${Client.getCFDParams()}`}
                  target={PROTOCOL_IFRAME}
                  variant="secondary"
                  disabled={ablyConnectionFailure}
                >
                  {`Connect to ${APP_NAME}`}
                </LinkButtonLightText>
              )}
            </Flex>
          </StyledFlexBox>
        </Flex>
      </CardSection>
    </Card>
  );
  return (
    <Flex flexDirection="column" flex="1 1 auto" ml={7}>
      {renderDeviceCard}
    </Flex>
  );
};

export { Hub };
